import { Box, Stack, Switch, Typography } from '@mui/material';
import React from 'react';
import {
  createDefaultToolBarPreferences,
  upgradeToolBarPreferences,
} from '../../config/plotPreferences';
import {
  ToolBarPreferences,
  ToolBarPreferencesV2,
} from '../../config/plotTypes';

// -- type definitions --

export interface ToolBarSettingsProps {
  value: ToolBarPreferences;
  onChange: (value: ToolBarPreferences) => void;
}

interface SettingRowProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

// -- functions --

const SettingRow: React.FC<SettingRowProps> = ({
  label,
  checked,
  onToggle,
  disabled = false,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ py: 0.5 }}
    >
      <Typography
        variant="body2"
        color={disabled ? 'text.disabled' : 'text.primary'}
      >
        {label}
      </Typography>
      <Switch
        checked={checked}
        onChange={onToggle}
        color="primary"
        disabled={disabled}
      />
    </Stack>
  );
};

// -- main component --

export const ToolBarSettings: React.FC<ToolBarSettingsProps> = ({
  value,
  onChange,
}) => {
  const resolvedValue = upgradeToolBarPreferences(value);

  const patch = (partial: Partial<ToolBarPreferencesV2>) => {
    const nextValue = createDefaultToolBarPreferences({
      ...resolvedValue,
      ...partial,
    });

    onChange(nextValue);
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{ fontSize: '0.9rem', fontWeight: 700 }}
        mb={1}
      >
        Paramètres de la barre d'outils
      </Typography>
      <Box
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          bgcolor: 'background.paper',
          px: 1.5,
          py: 0.75,
        }}
      >
        <SettingRow
          label="Cacher la barre d'outils"
          checked={resolvedValue.hide}
          onToggle={() => patch({ hide: !resolvedValue.hide })}
        />
        <SettingRow
          label="Afficher les raccourcis de plage temporelle"
          checked={resolvedValue.useTimeRangePills}
          disabled={resolvedValue.hide}
          onToggle={() =>
            patch({ useTimeRangePills: !resolvedValue.useTimeRangePills })
          }
        />
        <SettingRow
          label="Afficher le bouton de rechargement"
          checked={resolvedValue.useReloadButton}
          disabled={resolvedValue.hide}
          onToggle={() =>
            patch({ useReloadButton: !resolvedValue.useReloadButton })
          }
        />
        <SettingRow
          label="Afficher le sélecteur d'intervalle de rafraîchissement"
          checked={resolvedValue.useRefreshIntervalSelector}
          disabled={resolvedValue.hide}
          onToggle={() =>
            patch({
              useRefreshIntervalSelector:
                !resolvedValue.useRefreshIntervalSelector,
            })
          }
        />
      </Box>
    </Box>
  );
};
